import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../ui/dialog";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Textarea } from "../../../ui/textarea";
import { Switch } from "../../../ui/switch";
import { CateringMenuItem } from "../../../../hooks/useCateringMenus";

interface EditMenuItemDialogProps {
  item: CateringMenuItem | null;
  categoryId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (product: any, type: string, categoryID:string, updateID?: string) => void;
}

export function EditMenuItemDialog({ item, categoryId, open, onOpenChange, onSave }: EditMenuItemDialogProps) {
  const isCreate = !item?.id;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [pricePerPax, setPricePerPax] = useState<number | null>(null);
  const [isDefault, setIsDefault] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [tags, setTags] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setName(item.name);
      setDescription(item.description || "");
      setPricePerPax(item.price_per_pax);
      setIsDefault(item.is_default);
      setImageUrl(item.image_url || "");
      setTags(item.tags?.join(", ") || "");
    } else {
      setName("");
      setDescription("");
      setPricePerPax(null);
      setIsDefault(false);
      setImageUrl("");
      setTags("");
    }
  }, [item, open]);

  const handleSave = async () => {
   // setSaving(true);
    const tagsArray = tags
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t);

    const payload = {
      name,
      description: description || null,
      price_per_pax: pricePerPax,
      is_default: isDefault,
      image_url: imageUrl || null,
      tags: tagsArray.length > 0 ? tagsArray : null,
      allergens:[]
    };
    if (isCreate) {
      onSave(payload, 'add', categoryId)      
    }else {
      onSave(payload, 'update', categoryId, item.id)
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isCreate ? "Add Menu Item" : "Edit Menu Item"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto scrollbar-thin scrollbar-thumb-accent scrollbar-track-muted pr-2">
          <div className="space-y-2">
            <Label htmlFor="itemName">Item Name</Label>
            <Input
              id="itemName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Grilled Salmon"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the item"
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="pricePerPax">Price Per Pax (Upgrade)</Label>
            <Input
              id="pricePerPax"
              type="number"
              step="0.01"
              min={0}
              value={pricePerPax ?? ""}
              onChange={(e) => setPricePerPax(e.target.value ? parseFloat(e.target.value) : null)}
              placeholder="Leave empty if included"
            />
            <p className="text-xs text-muted-foreground">
              Set a price for premium upgrades, or leave empty for included items
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="imageUrl">Image URL</Label>
            <Input
              id="imageUrl"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="tags">Tags (comma separated)</Label>
            <Input
              id="tags"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="vegetarian, spicy, gluten-free"
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="isDefault">Default Selection</Label>
              <p className="text-xs text-muted-foreground">
                Auto-select this item in choice categories
              </p>
            </div>
            <Switch
              id="isDefault"
              checked={isDefault}
              onCheckedChange={setIsDefault}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !name.trim()}>
            {saving ? "Saving..." : isCreate ? "Create Item" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
